import type {
  IAPMAppDataService,
  InFieldUser,
  TaskInterval,
} from '@cognite/apm-client';
import { FDMClient } from '@cognite/fdm-client';
import type { CogniteClientDependencies } from '@cognite/fdm-client';

import {
  extractSourceableFields,
  getSourceCreatedFields,
  getSourceUpdatedFields,
} from '../../shared-utils/sourceable-utils';

import type { ScheduleUpsertType } from './cdm-types';
import type { IIntervalService } from './interval-service-interface';

function mapToScheduleUpsertType(
  interval: TaskInterval,
  userInstanceSpace: string
): ScheduleUpsertType {
  if (
    interval.startTime == null ||
    interval.endTime == null ||
    interval.timezone == null ||
    interval.freq == null ||
    interval.interval == null ||
    interval.template == null ||
    interval.templateItems == null
  ) {
    throw new Error(
      `Cannot map interval ${interval.externalId}: missing required schedule fields`
    );
  }

  return {
    ...extractSourceableFields(interval),
    externalId: interval.externalId,
    startTime: interval.startTime,
    endTime: interval.endTime,
    timezone: interval.timezone,
    freq: interval.freq,
    interval: interval.interval,
    byDay: interval.byDay,
    byMonth: interval.byMonth,
    status: interval.status,
    until: interval.until,
    exceptionDates: interval.exceptionDates,
    template: {
      externalId: interval.template.externalId,
      space: interval.template.space ?? userInstanceSpace,
    },
    templateItems: interval.templateItems.map((templateItem) => ({
      externalId: templateItem.externalId,
      space: templateItem.space ?? userInstanceSpace,
    })),
  };
}

export type APMClientDependencies = {
  intervals: Pick<
    IAPMAppDataService<ScheduleUpsertType>,
    'list' | 'upsert' | 'delete'
  >;
  userInstanceSpace: string;
};

export class CdmIntervalService extends FDMClient implements IIntervalService {
  apmClient: APMClientDependencies;
  constructor(
    client: CogniteClientDependencies,
    apmClient: APMClientDependencies
  ) {
    super(client);
    this.apmClient = apmClient;
  }

  async createIntervals(intervals: TaskInterval[], user: InFieldUser) {
    const upsertedIntervals = intervals.map((interval) => ({
      ...interval,
      ...getSourceCreatedFields(user),
    }));
    await this.upsertIntervals(upsertedIntervals);
  }

  async updateIntervals(intervals: TaskInterval[], user: InFieldUser) {
    const upsertedIntervals = intervals.map((interval) => ({
      ...interval,
      ...getSourceUpdatedFields(user),
    }));
    await this.upsertIntervals(upsertedIntervals);
  }

  async upsertIntervals(intervals: TaskInterval[]) {
    const intervalsToUpsert = intervals.map((interval) =>
      mapToScheduleUpsertType(interval, this.apmClient.userInstanceSpace)
    );
    await this.apmClient.intervals.upsert(intervalsToUpsert);
  }

  async deleteIntervals(externalIds: string[]) {
    await this.apmClient.intervals.delete(externalIds);
  }
}

export const createCdmIntervalService = (
  ...args: ConstructorParameters<typeof CdmIntervalService>
): IIntervalService => new CdmIntervalService(...args);
